import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Collapse } from '@mui/material';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import GenerationForm from './GenerationForm';
import { generationYopValidation } from './validation';
import useTypeOptions from '../../../hooks/useTypeOptions';
import validate from '../../../utils/validate';
import HideButton from '../../../components/hide-button/HideButton';
import {
  ASSISTANTS_CATEGORY,
  EMPTY_DROPDOWN_OPTION,
  mapScoutTabsToAssistantCategories,
  SCOUT_TABS,
} from '../../../constants';

import scss from './GenerationSection.module.scss';

export const emptyGenerationFormValues = {
  byPass: '',
  focusOn: '',
  url: '',
  assistant: EMPTY_DROPDOWN_OPTION,
  docs: null,
  fileText: '',
};

const GenerationSection = ({
  isShowGenerationForm,
  onShowGenerationForm,
  onGenerateMessages,
  onGenerateMore,
  onCompressMessages,
  onReply,
  onResetSession,
  onFileUpload,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation('scout');

  const abortAction = useRef(null);
  const generationForm = useSelector((state) => state.scout.generationForm);
  const scoutTab = useSelector((state) => state.scout.scoutTab);

  const typeOptions = useTypeOptions(
    (state) => state.scout.assistantsData?.[mapScoutTabsToAssistantCategories[scoutTab]],
  );

  const selectedTypeOption = useCallback(
    (assistantCategory) => {
      if (!generationForm.channels[assistantCategory]?.dbAssistantId) {
        return emptyGenerationFormValues.assistant;
      }

      return typeOptions.find(({ value }) => value === generationForm.channels[assistantCategory].dbAssistantId);
    },
    [generationForm.channels, typeOptions],
  );

  const initialGenerationFormValuesValues = useMemo(
    () => ({
      byPass: generationForm.byPass || emptyGenerationFormValues.byPass,
      focusOn: generationForm.focusOn || emptyGenerationFormValues.focusOn,
      url: generationForm.url || emptyGenerationFormValues.url,
      docs: null,
      fileText: generationForm.fileText || '',

      channels: {
        [ASSISTANTS_CATEGORY.GENERATE_ALL]: {
          assistant: selectedTypeOption(ASSISTANTS_CATEGORY.GENERATE_ALL) || emptyGenerationFormValues.assistant,
        },
        [ASSISTANTS_CATEGORY.GENERATE_SELECTED]: {
          assistant: selectedTypeOption(ASSISTANTS_CATEGORY.GENERATE_SELECTED) || emptyGenerationFormValues.assistant,
        },
        [ASSISTANTS_CATEGORY.COMPRESS]: {
          assistant: selectedTypeOption(ASSISTANTS_CATEGORY.COMPRESS) || emptyGenerationFormValues.assistant,
        },
      },
    }),
    [generationForm, selectedTypeOption],
  );

  const handleSubmitGenerationForm = (values, form) => {
    const { dirty } = form.getState();

    let resultAction;

    if (scoutTab === SCOUT_TABS.ALL_MESSAGES && dirty) {
      resultAction = dispatch(onGenerateMessages({ id, values }));
    }

    if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
      resultAction = dispatch(onGenerateMore({ id, values }));
    }

    if (scoutTab === SCOUT_TABS.FINAL_MESSAGES) {
      resultAction = dispatch(onCompressMessages({ id, values }));
    }

    abortAction.current = resultAction?.abort;
  };

  useEffect(() => {
    return () => {
      if (abortAction.current) {
        abortAction.current();
      }
    };
  }, []);

  return (
    <div className={scss.generationSection}>
      <HideButton
        isShow={isShowGenerationForm}
        onToggleVisibility={onShowGenerationForm}
        label={t('openGenerationForm')}
      />

      <Collapse in={isShowGenerationForm}>
        <div className={scss.generationWrapper}>
          <Form
            initialValues={initialGenerationFormValuesValues}
            onSubmit={handleSubmitGenerationForm}
            subscription={{ submitting: true, pristine: true }}
            keepDirtyOnReinitialize
            validate={(values) => validate(values, generationYopValidation(scoutTab))}
            render={({ handleSubmit }) => (
              <GenerationForm
                onSubmit={handleSubmit}
                onReply={onReply}
                onResetSession={onResetSession}
                onFileUpload={onFileUpload}
              />
            )}
          />
        </div>
      </Collapse>
    </div>
  );
};

export default memo(GenerationSection);
